import React, { createContext, useContext, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext"; // adjust path as needed

// ─── WorkspaceContext ──────────────────────────────────────────────────────────
// Tracks which workspace the user is operating in: "company" | "consultant"
// Persisted to sessionStorage so refreshes don't reset it.
// ─────────────────────────────────────────────────────────────────────────────

const WorkspaceContext = createContext(null);

export const WORKSPACES = {
  COMPANY: "company",
  CONSULTANT: "consultant",
};

// ── API base URLs per workspace ───────────────────────────────────────────────
// These read your existing env vars.  Add REACT_APP_API_CONSULTANT_URL to .env
// if it differs from REACT_APP_API_HR_URL.
export const WORKSPACE_API = {
  company: {
    base: process.env.REACT_APP_API_HR_URL, // http://localhost:8080/api/recruiter-hr
    manager: process.env.REACT_APP_API_MANAGER_URL, // http://localhost:8080/api/manager
    hod: process.env.REACT_APP_API_HOD_URL, // http://localhost:8080/api/hod
    common: process.env.REACT_APP_COMMON_API_URL, // http://localhost:8080/api/emp
  },
  consultant: {
    base:
      process.env.REACT_APP_API_CONSULTANT_URL || // http://localhost:8080/api/consultant
      process.env.REACT_APP_API_HR_URL, // fallback until env var is set
    common: process.env.REACT_APP_COMMON_API_URL,
  },
};

export const WorkspaceProvider = ({ children }) => {
  const stored = sessionStorage.getItem("workspace");
  const [workspace, setWorkspaceState] = useState(
    Object.values(WORKSPACES).includes(stored) ? stored : null, // null = not yet chosen
  );
  const { hasAuthority } = useAuth();

  const setWorkspace = useCallback((ws) => {
    sessionStorage.setItem("workspace", ws);
    setWorkspaceState(ws);
  }, []);

  const clearWorkspace = useCallback(() => {
    sessionStorage.removeItem("workspace");
    setWorkspaceState(null);
  }, []);

  const isCompany = workspace === WORKSPACES.COMPANY;
  const isConsultant = workspace === WORKSPACES.CONSULTANT;
  const isConsultantAdmin = hasAuthority("ROLE_CONSULTANT-ADMIN");
  const isRecruiterHr = hasAuthority("ROLE_RECRUITER-HR");
  const api = workspace ? WORKSPACE_API[workspace] : WORKSPACE_API.company;

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        setWorkspace,
        clearWorkspace,
        isCompany,
        isConsultant,
        isConsultantAdmin,
        isRecruiterHr,
        api,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx)
    throw new Error("useWorkspace must be used inside WorkspaceProvider");
  return ctx;
};
